import { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { docRegisterI18n } from './i18n';
import CardStats from '../../components/CardStats/CardStats';
import ModalRegisterDetails from './partials/ModalRegisterDetails/ModalRegisterDetails';
import { getDocumentSendStats } from '../../services/documentManagementServices';
import './documentRegister.css';
import {
  getDocumentSendPagedRegister,
  getDocumentSendStatus,
} from '../../services/documentRegisterServices';
import { formatDate } from '../../utils/formatDate';
import { getCurrentUser } from '../../services/authService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { excelExportLogService } from 'services/excelLogFromFront';
import usePermissionsSmartDocuments from 'permissions/modules/smartDocuments/usePermissionsSmartDocuments';

const DocumentRegister = ({ DesignSystemComponents }) => {
  const location = useLocation();
  const { Input, Dropdown, Badge, Table, IconButton, Card, Button } =
    DesignSystemComponents;

  const { t, language: drLanguage } = useScopedTranslation(docRegisterI18n);

  const SORT_HUMAN_LABELS = useMemo(
    () => ({
      name: t('dr_sortDocument'),
      caseIdentifier: t('dr_sortCase'),
      solicitorName: t('dr_sortCreator'),
      createdAt: t('dr_sortCreatedAt'),
      statusChangedByName: t('dr_sortReviewer'),
      statusChangedAt: t('dr_sortReviewedAt'),
      deliveryTypeIdDocument: t('dr_sortDeliveryType'),
      statusName: t('dr_sortStatus'),
    }),
    [drLanguage]
  );

  const getStatusBadgeColor = (statusName) => {
    if (statusName === 'Cargado') return 'success';
    if (statusName === 'Pendiente en PJUD') return 'warning';
    if (statusName === 'Rechazado') return 'error';
    return 'light';
  };

  const getStatusDisplayName = (statusName) => {
    if (statusName === 'Pendiente en PJUD') return t('dr_status_pending');
    if (statusName === 'Cargado') return t('dr_status_loaded');
    if (statusName === 'Rechazado') return t('dr_status_rejected');
    return statusName;
  };

  const [userId, setUserId] = useState(null);

  const [showModalRegisterDetails, setShowModalRegisterDetails] =
    useState(false);
  const [selectedRegisterId, setSelectedRegisterId] = useState(null);

  const [loading, setLoading] = useState(false);

  const [totalDocuments, setTotalDocuments] = useState('');
  const [totalLoaded, setTotalLoaded] = useState('');
  const [totalPendingInPjud, setTotalPendingInPjud] = useState('');
  const [totalRejected, setTotalRejected] = useState('');

  const [stateList, setStateList] = useState([]);
  const [loadStates, setLoadStates] = useState({
    label: t('dr_allStates'),
    value: null,
  });
  const [statusId, setStatusId] = useState(null);

  const [totalRegistered, setTotalRegistered] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [documentRegisterTablePage, setDocumentRegisterTablePage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [searchDraft, setSearchDraft] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const stateSearch = location.state?.search ?? '';

  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const { canDownloadRegistryExcel, canViewDetail } =
    usePermissionsSmartDocuments();

  const debounceRef = useRef(null);

  const handleSearchChange = (text) => {
    setSearchDraft(text);
    setLoading(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setDocumentRegisterTablePage(0);
      setSearchTerm(text.trim());
    }, 350);
  };

  const fetchDocumentSendStats = async () => {
    try {
      setLoading(true);
      const result = await getDocumentSendStats.getStats();
      setTotalDocuments(result.totalDocuments);
      setTotalLoaded(result.totalLoaded);
      setTotalPendingInPjud(result.totalPendingInPjud);
      setTotalRejected(result.totalRejected);
    } catch (error) {
      console.warn('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentRegisterStatus = async () => {
    try {
      const result = await getDocumentSendStatus.getStatus();

      const mapped = [
        { label: t('dr_allStates'), value: null },
        ...result.map((item) => ({
          label: item.name.replace(' en PJUD', ''),
          value: item.id,
        })),
      ];

      setStateList(mapped);
      setLoadStates(mapped[0]);
      setStatusId(null);
    } catch (error) {
      console.warn('Error loading statuses:', error);
      setStateList([]);
    }
  };

  const fetchDocumentSendPaged = async () => {
    try {
      setLoading(true);

      const result = await getDocumentSendPagedRegister.getPagedRegister(
        documentRegisterTablePage + 1,
        pageSize,
        searchTerm,
        statusId,
        sortKey,
        sortDirection
      );

      setTotalRegistered(result.items);
      setTotalCount(result.total);
    } catch (error) {
      console.warn('Error loading register:', error);
      setTotalRegistered([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRegisters = async () => {
    const pageSizeToUse = 200;
    let page = 1;
    let allItems = [];
    let totalItems = 0;

    try {
      while (true) {
        const result = await getDocumentSendPagedRegister.getPagedRegister(
          page,
          pageSizeToUse,
          searchTerm,
          statusId,
          sortKey,
          sortDirection
        );

        const items = result.items || [];
        const total = result.total || 0;

        if (page === 1) totalItems = total;

        allItems = [...allItems, ...items];

        if (allItems.length >= totalItems) break;

        page++;
      }
    } catch (error) {
      console.error('Error fetching all registers:', error);
    }

    return allItems;
  };

  const buildHumanFilters = () => {
    const filters = {};

    if (searchTerm?.trim()) {
      filters[t('dr_filterSearchBy')] = searchTerm.trim();
    }

    if (statusId !== null && loadStates?.label) {
      filters[t('dr_filterStatus')] = loadStates.label;
    }

    return filters;
  };

  const downloadExcel = async () => {
    try {
      await excelExportLogService.log({
        userId,
        source: t('dr_excelSource'),
        sortKey: sortKey ? (SORT_HUMAN_LABELS[sortKey] ?? sortKey) : null,
        sortDesc: sortDirection === 'desc' ? true : false,
        filters: buildHumanFilters(),
      });
    } catch (error) {
      console.warn('Export log failed, continuing with download.', error);
    }

    try {
      const allData = await fetchAllRegisters();
      if (!allData.length) return;

      const formatted = allData.map((item) => ({
        [t('dr_excelCol_document')]: item.name,
        [t('dr_excelCol_docType')]: item.documentType,
        [t('dr_excelCol_case')]: item.caseIdentifier,
        [t('dr_excelCol_role')]: item.caseCode,
        [t('dr_excelCol_creator')]: item.solicitorName,
        [t('dr_excelCol_createdAt')]: item.createdAt
          ? formatDate(item.createdAt)
          : '-',
        [t('dr_excelCol_reviewer')]: item.statusChangedByName || '-',
        [t('dr_excelCol_reviewedAt')]: item.statusChangedAt
          ? formatDate(item.statusChangedAt)
          : '-',
        [t('dr_excelCol_deliveryType')]:
          item.deliveryTypeIdDocument === 1
            ? t('dr_manual')
            : t('dr_automatic'),
        [t('dr_excelCol_status')]: getStatusDisplayName(item.statusName),
      }));

      const worksheet = XLSX.utils.json_to_sheet(formatted);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, t('dr_excelSheet'));

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });

      saveAs(blob, t('dr_excelFile'));
    } catch (error) {
      console.error('Error generating or downloading Excel:', error);
    }
  };

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    } else {
      console.warn('No se pudo obtener el usuario autenticado');
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchDocumentSendStats();
    fetchDocumentRegisterStatus();
  }, [userId]);

  useEffect(() => {
    if (!stateList.length) return;
    const updatedList = stateList.map((item) =>
      item.value === null ? { ...item, label: t('dr_allStates') } : item
    );
    setStateList(updatedList);
    if (loadStates?.value === null) {
      setLoadStates(updatedList[0]);
    }
  }, [drLanguage]);

  const handleChangeState = (selected) => {
    setLoadStates(selected);
    setStatusId(selected.value);
    setDocumentRegisterTablePage(0);
  };

  const refreshTable = () => {
    fetchDocumentSendStats();
    fetchDocumentSendPaged();
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    setSortKey(key);
    setSortDirection(direction);
    setDocumentRegisterTablePage(0);
  };

  useEffect(() => {
    if (stateSearch) {
      setSearchDraft(stateSearch);
      setSearchTerm(stateSearch.trim());
      setDocumentRegisterTablePage(0);
    }
  }, [stateSearch]);

  useEffect(() => {
    if (!userId) return;
    fetchDocumentSendPaged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId,
    documentRegisterTablePage,
    pageSize,
    searchTerm,
    statusId,
    sortKey,
    sortDirection,
  ]);

  return (
    <div>
      <div className='mb-4'>
        <h2 className=' fw-semibold'>{t('dr_title')}</h2>
        <h4 className=' fw-normal'>{t('dr_subtitle')}</h4>
      </div>
      <div className='row mb-4'>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dr_statTotal')}
            count={totalDocuments}
            subtitle={t('dr_statTotalSub')}
            icon='receipt'
            type='info'
            loading={loading}
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dr_statLoaded')}
            count={totalLoaded}
            subtitle={t('dr_statLoadedSub')}
            icon='check2Circle'
            type='success'
            loading={loading}
            fill
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dr_statPending')}
            count={totalPendingInPjud}
            subtitle={t('dr_statPendingSub')}
            icon='clock'
            type='warning'
            loading={loading}
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dr_statRejected')}
            count={totalRejected}
            subtitle={t('dr_statRejectedSub')}
            icon='close'
            type='error'
            loading={loading}
          />
        </div>
      </div>
      <Card specialPadding='px-4 pt-4 pb-2'>
        <div className='row'>
          <div className='col-3 d-flex align-items-center'>
            <h3 className='fw-semibold mb-3'>{t('dr_historyTitle')}</h3>
          </div>
          <div className='col-4'>
            <Input
              icon='search'
              placeholder={t('dr_searchPlaceholder')}
              type='text'
              value={searchDraft}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className='col-3'>
            <Dropdown
              options={stateList}
              value={loadStates}
              onChange={handleChangeState}
            />
          </div>
          <div className='col-2 d-flex align-items-center'>
            <Button
              iconStart='download'
              label={t('dr_downloadExcel')}
              variant='naked'
              size='sm'
              className='mb-4'
              onClick={downloadExcel}
              disabled={loading || !canDownloadRegistryExcel}
            />
          </div>
        </div>
        <div className='table-container table-record-loaded'>
          <Table
            columns={[
              {
                accessor: 'name',
                label: t('dr_col_document'),
                sort: true,
                render: (info) => (
                  <div>
                    <p className='caption fw-normal '>{info.name}</p>
                    <p className='mini fw-normal font-light'>
                      {info.documentType}
                    </p>
                  </div>
                ),
              },
              {
                accessor: 'caseIdentifier',
                label: t('dr_col_case'),
                sort: true,
                render: (info) => (
                  <div>
                    <p className='caption fw-normal '>{info.caseIdentifier}</p>
                    <p className='mini fw-normal font-light'>
                      {t('dr_col_role', { code: info.caseCode })}
                    </p>
                  </div>
                ),
              },
              {
                accessor: 'solicitorName',
                label: t('dr_col_creator'),
                sort: true,
              },
              {
                accessor: 'createdAt',
                label: t('dr_col_createdAt'),
                sort: true,
                render: (info) =>
                  info.createdAt === null ? '-' : formatDate(info.createdAt),
              },
              {
                accessor: 'statusChangedByName',
                label: t('dr_col_reviewer'),
                sort: true,
                render: (info) =>
                  info.statusChangedByName === null
                    ? '-'
                    : info.statusChangedByName,
              },
              {
                accessor: 'statusChangedAt',
                label: t('dr_col_reviewedAt'),
                sort: true,
                render: (info) => (
                  <p className='caption fw-normal '>
                    {info.statusChangedAt === null
                      ? '-'
                      : formatDate(info.statusChangedAt)}
                  </p>
                ),
              },
              {
                accessor: 'deliveryTypeIdDocument',
                label: t('dr_col_deliveryType'),
                sort: true,
                render: (info) => (
                  <Badge
                    text={
                      info.deliveryTypeIdDocument === 1
                        ? t('dr_manual')
                        : t('dr_automatic')
                    }
                    color={info.deliveryTypeIdDocument === 1 ? 'info' : 'light'}
                    size='sm'
                  />
                ),
              },
              {
                accessor: 'statusName',
                label: t('dr_col_status'),
                sort: true,
                render: (info) => (
                  <Badge
                    text={getStatusDisplayName(info.statusName)}
                    color={getStatusBadgeColor(info.statusName)}
                    size='sm'
                  />
                ),
              },
              {
                label: t('dr_col_actions'),
                render: (info) => (
                  <div className='d-flex'>
                    <IconButton
                      ariaLabel={t('dr_aria_viewDetail')}
                      icon='eye'
                      onClick={() => {
                        setSelectedRegisterId(info.id);
                        setShowModalRegisterDetails(true);
                      }}
                      size='md'
                      iconStroke='var(--primary-500)'
                      disabled={!canViewDetail}
                    />
                  </div>
                ),
              },
            ]}
            data={totalRegistered}
            initialPageSize={pageSize}
            page={documentRegisterTablePage}
            onPageChange={setDocumentRegisterTablePage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setDocumentRegisterTablePage(0);
            }}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
            paginationMode='server'
            totalItems={totalCount}
            loading={loading}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={handleServerSortChange}
            moreLines
          />
        </div>
      </Card>

      <ModalRegisterDetails
        DesignSystemComponents={DesignSystemComponents}
        show={showModalRegisterDetails}
        onClose={() => setShowModalRegisterDetails(false)}
        documentId={selectedRegisterId}
        onUpdated={refreshTable}
        userId={userId}
      />
    </div>
  );
};

export default DocumentRegister;
